import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

// Importamos el formulario centralizado
import FormularioPoliza from '../Formularios/FormularioPoliza';

// Importamos el componente de alerta preventiva
import AlertaDeudaCliente from '../alertas/AlertaDeudaCliente';

const ModalEmision = ({ show, onClose, onSaveSuccess, clientData, initialData }) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setIsReady(true), 150);
      return () => clearTimeout(timer);
    } else {
      setIsReady(false);
    }
  }, [show]);

  // --- RESOLUCIÓN DEL OBJETO CLIENTE PARA LA ALERTA ---
  const resolvedClient = useMemo(() => {
    if (clientData) return clientData;
    if (initialData?.clienteId || initialData?.cliente) {
      return {
        id: initialData.clienteId || initialData.cliente?.id || initialData.id,
        tieneDeuda: initialData.clienteTieneDeuda ?? initialData.tieneDeuda ?? false,
        cantidadPolizasConDeuda: initialData.cantidadPolizasConDeuda || 0
      };
    }
    return null;
  }, [clientData, initialData]);

  // --- LÓGICA DE RESOLUCIÓN DE NOMBRE PARA EL HEADER ---
  const displayClientName = useMemo(() => {
    // 1. Si hay initialData (Edición), priorizamos lo que dice la póliza
    if (initialData) {
      const nombrePol = `${initialData.clienteNombre || ''} ${initialData.clienteApellido || ''}`.trim();
      if (nombrePol) return nombrePol.toUpperCase();
      if (initialData.clienteNombreCompleto) return initialData.clienteNombreCompleto.toUpperCase();
    }

    // 2. Si no hay póliza o está vacía, usamos el clientData (Emisión Nueva)
    if (clientData) {
      const nombreCli = `${clientData.nombre || ''} ${clientData.apellido || ''}`.trim();
      return nombreCli ? nombreCli.toUpperCase() : 'S/D';
    }

    return 'SIN CLIENTE ASIGNADO';
  }, [initialData, clientData]);

  if (!show) return null;

  const handleFormSuccess = (msg, payload) => {
    if (onSaveSuccess) onSaveSuccess(msg, payload);
    onClose();
  };

  const title = initialData ? 'Editar Póliza' : 'Nueva Emisión';

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1010] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity">
      
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-full max-h-[95vh] flex flex-col overflow-hidden animate-fade-in border border-gray-100">
        
        {/* HEADER DEL MODAL */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-blue-50/50 shrink-0">
            <div className="flex items-center gap-3 text-left">
                <span className="bg-blue-100 text-blue-600 p-2 rounded-lg shrink-0">
                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                </span>
                <div>
                    <h3 className="text-xl font-bold text-gray-900 leading-none">{title}</h3>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs font-medium text-gray-500">
                        <span>Asegurado: <span className="text-blue-700 font-bold">{displayClientName}</span></span>
                        {initialData?.nPoliza && (
                           <>
                             <span className="text-gray-300">|</span>
                             <span>Póliza: <span className="font-bold text-gray-800">{initialData.nPoliza}</span></span>
                           </>
                        )}
                    </div>
                </div>
            </div>

            <button 
                onClick={onClose} 
                className="group text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-white/80 cursor-pointer"
            >
                <svg className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
        </div>

        {/* ⚠️ BANNER DE ADVERTENCIA DE DEUDA (Instantáneo, ubicado en cabecera) */}
        {resolvedClient?.tieneDeuda && (
            <div className="px-6 pt-4 shrink-0">
                <AlertaDeudaCliente client={resolvedClient} />
            </div>
        )}

        {/* CUERPO DEL MODAL */}
        <div className="relative flex-grow overflow-hidden flex flex-col">
            {!isReady ? (
                <div className="absolute inset-0 bg-white z-50 flex flex-col items-center justify-center gap-3">
                    <div className="w-10 h-10 border-4 border-blue-50 border-t-blue-500 rounded-full animate-spin"></div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Iniciando formulario...</p>
                </div>
            ) : (
                <div className="flex-grow overflow-hidden flex flex-col">
                    <FormularioPoliza 
                        clientData={clientData}
                        initialData={initialData}
                        onCancel={onClose}
                        onSuccess={handleFormSuccess}
                        tipoCarga="PATRIMONIALES" 
                        mode={initialData ? 'edit' : 'create'}
                    />
                </div>
            )}
        </div>
      </div>

      <style>{`
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>,
    document.body
  );
};

export default ModalEmision;